import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import { VISIT_TYPES } from '../../utils/constants';
import { exportActionItemReportToExcel } from '../../utils/exportToExcel';
import Alert from '../Common/Alert';
import Spinner from '../Common/Spinner';
import './ReportsHome.css';
import './AuditReport.css';

const ACTION_ITEM_REPORT_COLUMNS = [
  { key: 'srNo', label: 'Sr No.' },
  { key: 'visitId', label: 'Visit ID' },
  { key: 'actionItem', label: 'Issue' },
  { key: 'visitType', label: 'Visit Type' },
  { key: 'section', label: 'Section' },
  { key: 'attribute', label: 'Attribute' },
  { key: 'status', label: 'Status' },
  { key: 'createdBy', label: 'Created By' },
  { key: 'createdOn', label: 'Created On' },
  { key: 'createdOffset', label: 'Created Offset' },
  { key: 'performedBy', label: 'Performed By' },
  { key: 'performedOn', label: 'Performed On UTC' },
  { key: 'performedAtOffset', label: 'Performed At Offset' },
];

/** Excel export: Sr No. + visit/attribute/status/performed columns; exclude Action Item, Section, Created By, Created On, Created Offset */
const ACTION_ITEM_REPORT_EXPORT_COLUMNS = ACTION_ITEM_REPORT_COLUMNS.filter(
  (c) =>
    c.key !== 'actionItem' &&
    c.key !== 'section' &&
    c.key !== 'createdBy' &&
    c.key !== 'createdOn' &&
    c.key !== 'createdOffset'
);

/** Column widths: Sr No., Visit ID, Visit Type, Attribute, Status, Performed By, Performed On UTC, Performed At Offset */
const ACTION_ITEM_EXPORT_COL_WIDTHS = [7, 14, 26, 28, 14, 36, 20, 18];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Performed On display format: 16-Mar-2026 11:24 */
function formatPerformedOn(value) {
  if (value == null) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = SHORT_MONTHS[d.getMonth()] || '';
  const year = d.getFullYear();
  const hour = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hour}:${min}`;
}

function formatReportDate(value) {
  if (value == null) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Excel Visit Type: full name (e.g. SSV → Site Selection Visit); pass through if already full text. */
function formatVisitTypeForExport(value) {
  if (value == null) return '';
  const v = String(value).trim();
  if (!v) return '';
  const code = v.toUpperCase();
  if (VISIT_TYPES[code]) return VISIT_TYPES[code];
  return v;
}

const ActionItemReport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vRecordedSign, vRecordedAtOffset } = useSession();
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all' ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode) : '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    const params = {};
    if (projectCode) params.projectCode = projectCode;
    api
      .get('/Report/action-items', { params })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setRows(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Failed to load issues.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleExportExcel = () => {
    setError(null);
    setExportLoading(true);
    const exportKeys = ACTION_ITEM_REPORT_EXPORT_COLUMNS.map((c) => c.key);
    const exportRows = rows.map((r, idx) => {
      const row = {};
      exportKeys.forEach((k) => {
        if (k === 'srNo') {
          row[k] = idx + 1;
        } else if (k === 'visitId') {
          row[k] = r.visitId ?? r.VisitId ?? '';
        } else if (k === 'performedOn') {
          row[k] = r[k] ? formatPerformedOn(r[k]) : '';
        } else if (k === 'visitType') {
          row[k] = formatVisitTypeForExport(r.visitType ?? r.VisitType);
        } else {
          row[k] = r[k] ?? '';
        }
      });
      return row;
    });
    try {
      const fileName = `Issue_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const firstName = (user?.firstName ?? user?.FirstName ?? user?.vFirstName ?? '').trim();
      const lastName = (user?.lastName ?? user?.LastName ?? user?.vLastName ?? '').trim();
      const userName = (user?.username ?? user?.Username ?? user?.vLoginName ?? user?.vUserName ?? '').trim();
      const profile = (user?.role ?? user?.Role ?? user?.profileCode ?? user?.vUserTypeCode ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      const exportedBy = fullName ? `${fullName} (${userName || '—'})(${profile || '—'})` : (vRecordedSign && vRecordedSign.trim() !== '' ? vRecordedSign.trim() : '');
      const offset = (vRecordedAtOffset && String(vRecordedAtOffset).trim() !== '') ? String(vRecordedAtOffset).trim() : '+5:30';
      exportActionItemReportToExcel(exportRows, ACTION_ITEM_REPORT_EXPORT_COLUMNS, fileName, {
        formName: 'Issue Report',
        exportedBy,
        exportedOnUtc: formatReportDate(new Date()),
        offset,
        readOnlyAllowResize: true,
        colWidths: ACTION_ITEM_EXPORT_COL_WIDTHS,
      });
    } catch (err) {
      setError(err?.message || 'Export failed.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="audit-report">
      <div className="audit-report-header" style={{ marginBottom: '16px' }}>
        <h2>Issue Report</h2>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="audit-report-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExportExcel}
          disabled={exportLoading || loading || rows.length === 0}
          title={exportLoading ? 'Exporting…' : 'Export to Excel'}
        >
          {exportLoading ? 'Exporting…' : 'Export'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/reports')}>
          Close
        </button>
      </div>      
    </div>
  );
};

export default ActionItemReport;
